import { useMemo } from "react";
import { Chart } from "react-chartjs-2";
import * as ChartGeo from "chartjs-chart-geo";
import {
  Chart as ChartJS,
  CategoryScale,
  Tooltip,
  Title,
  Legend,
} from "chart.js";
import { Box } from "@mui/material";
import WidgetCard from "@arelia-components/ui-atoms/WidgetCard";
import { geodata } from "src/widgets/countries/geodata";
import { calcPercentage, large, medium, small, zero } from "src/widgets/countries/utils";
import getChartOptions from "src/widgets/countries/options";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  ChartGeo.ChoroplethController,
  ChartGeo.ProjectionScale,
  ChartGeo.ColorScale,
  ChartGeo.GeoFeature
);

type Props = {
  countries: Record<string, number>;
  loading: boolean;
  id: string;
};

export default function CustomerLocationsPresenter({ countries, loading, id }: Props) {
  const calcBackgroundColor = (average: Record<string, { value: number; percentage: number }>, key: string): string => {
    return average[key]?.percentage >= 20 ? large : average[key]?.percentage >= 1 ? medium : average[key]?.percentage > 0 ? small : zero;
  }

  const valueByCountry = useMemo(() => {
    const countryNames = geodata.objects.countries1.geometries.filter(x => x.properties["Alpha-2"])
      .map(c => ({ name: c.properties.name, code: c.properties["Alpha-2"]})) as { code: string, name: string }[];

      const total = Object.values(countries).reduce((acc, country) => acc + country, 0);

      return countryNames.reduce((obj, countryName) => {
        const countryValue = +(countries[countryName.code] || countries[countryName.name] || 0);
        obj[countryName.code] = {
          value: countryValue,
          percentage: +(calcPercentage(countryValue, total)).toFixed(2)
        };
        return obj;
      }, {} as Record<string, { value: number; percentage: number }> );
  }, [countries]);

  const data = useMemo(() =>
    ChartGeo.topojson.feature(
      //@ts-ignore
      geodata,
      geodata.objects.countries1
      //@ts-ignore
    ).features.map((d: any) => {
      return ({
        ...d, properties: {
          ...d.properties,
          backgroundColor: calcBackgroundColor(valueByCountry, d.properties["Alpha-2"])
        }
      })
    }), [valueByCountry]);

  const chartData = useMemo(() => {
    return {
      labels: data.map((d: any) => d.properties.name),
      datasets: [
        {
          outline: data,
          label: "Countries",
          backgroundColor: data.map((d: any) => d.properties.backgroundColor),
          data: data.map((d: any) => {
            return {
              feature: d,
              value: valueByCountry?.[d.properties["Alpha-2"]] || 0,
              label: d.properties.name,
              countryKey: d.properties["Alpha-2"],
            };
          }),
        },
      ],
    }
  }, [data, valueByCountry]);

  const tooltipTitleFunction = () => `Country`;
  const tooltipLabelFunction = (tooltipItem: any) => {
    const { percentage, value } = tooltipItem.raw.value;
    return `${tooltipItem.raw.label}: ${value} (${percentage}%)`;
  }

  const options = getChartOptions(tooltipTitleFunction, tooltipLabelFunction);

  return (
    <WidgetCard
      widgetId={id}
      title="Top Countries"
      loading={loading}
      disableFavorite={true}
    >
      <Box width={1} >
        <Chart
          type="choropleth"
          data={chartData}
          options={options}
        />
      </Box>
    </WidgetCard>
  );
}
